import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Check } from 'lucide-react';
import { COMPANY, SERVICES, HOW_IT_WORKS, WHY_CHOOSE } from '../mock';

const Home = () => {
  return (
    <div>
      {/* HERO */}
      <section className="ea-hero-gradient relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
          <div className="ea-fade-up">
            <h1 className="font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-[3.4rem]">
              Professional Home & Property Services Across London
            </h1>
            <p className="mt-5 text-lg font-medium text-white/90">
              Plumbing • Electrical • Painting • Cleaning • HVAC
            </p>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/80">
              Reliable, vetted professionals at your door. Tailored quotes for every job — no hidden fees, no surprises. Just quality service you can trust.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/request-a-service"
                className="rounded-full bg-white px-7 py-3.5 text-[15px] font-bold text-[#1b4f72] shadow-lg transition-transform hover:-translate-y-0.5"
              >
                Request a Service
              </Link>
              <Link
                to="/our-services"
                className="rounded-full border-2 border-white/70 px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-white/10"
              >
                View Services
              </Link>
            </div>
          </div>

          <div className="ea-fade-up flex justify-center">
            <div className="rounded-3xl bg-white p-8 shadow-2xl">
              <img src={COMPANY.logo} alt="E.A. Lead Solutions Logo" className="h-64 w-64 object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#e9f3ec] py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="ea-heading text-3xl font-bold sm:text-4xl">Our Services</h2>
            <p className="mt-3 text-gray-500">Expert solutions for every property need</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.id} className="ea-card-hover rounded-2xl bg-white p-8 text-center shadow-sm">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#e9f3ec]">
                    <Icon className="h-8 w-8 text-[#3a9b5c]" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold text-[#1b4f72]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{s.short}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <Link to="/request-a-service" className="inline-block rounded-full bg-[#3a9b5c] px-8 py-3.5 font-semibold text-white shadow-md transition-all hover:bg-[#318150]">
              Request a Service
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="ea-heading text-3xl font-bold sm:text-4xl">How It Works</h2>
            <p className="mt-3 text-gray-500">Three simple steps to get the help you need</p>
          </div>
          <div className="mt-14 grid gap-10 md:grid-cols-3">
            {HOW_IT_WORKS.map((h) => (
              <div key={h.step} className="text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#e9f3ec] font-display text-2xl font-bold text-[#3a9b5c]">
                  {h.step}
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-gray-800">{h.title}</h3>
                <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-gray-600">{h.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-[#e9f3ec] py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="ea-heading text-center text-3xl font-bold sm:text-4xl">Why Choose E.A. Lead Solutions?</h2>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_CHOOSE.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-white shadow-sm">
                    <Icon className="h-7 w-7 text-[#3a9b5c]" />
                  </div>
                  <h3 className="mt-5 font-display text-base font-bold text-gray-800">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ea-cta-gradient py-16">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">Ready to Get Started?</h2>
          <p className="mt-4 text-white/80">Professional services across London — request your free quote today.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/request-a-service" className="rounded-full bg-[#3a9b5c] px-8 py-3.5 font-semibold text-white shadow-md transition-all hover:bg-[#46ad6a]">
              Request a Service
            </Link>
            <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-full border-2 border-white/60 px-8 py-3.5 font-semibold text-white transition-colors hover:bg-white/10">
              <MessageCircle size={18} /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
